/**
 * Hand-evaluation primitives shared across samplers and classifiers.
 *
 * This module covers detection only (is there a flush? a straight? a pair?) —
 * not full 7-card hand ranking. Full showdown evaluation comes with the engine
 * state machine in a later subslice.
 *
 * Per game engine spec §9: "no ace-low usage in V1." The wheel (A-2-3-4-5) is
 * intentionally NOT counted as a straight. This is a deliberate V1 limitation
 * documented in /design/specs/5_game_engine_spec.md §1.1.
 */

import { type Card, type Rank, RANK_VALUE, type Suit } from '@/types/card';

// =============================================================================
// Rank/suit helpers
// =============================================================================

/** All 10 V1-recognized straights (high-card rank of each). No wheel in V1. */
export const STRAIGHTS_BY_HIGH: readonly Rank[] = [
  '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A',
];

/** Return the 5 ranks (numeric values) of the straight with high card `high`. */
export function straightRanksFor(high: Rank): number[] {
  const top = RANK_VALUE[high];
  return [top, top - 1, top - 2, top - 3, top - 4];
}

/** Count cards by rank → record of rank → count. */
export function rankCounts(cards: readonly Card[]): Map<Rank, number> {
  const m = new Map<Rank, number>();
  for (const c of cards) m.set(c.rank, (m.get(c.rank) ?? 0) + 1);
  return m;
}

/** Count cards by suit → record of suit → count. */
export function suitCounts(cards: readonly Card[]): Map<Suit, number> {
  const m = new Map<Suit, number>();
  for (const c of cards) m.set(c.suit, (m.get(c.suit) ?? 0) + 1);
  return m;
}

/** Highest card on board by rank value. Throws if `cards` is empty. */
export function topRank(cards: readonly Card[]): Rank {
  if (cards.length === 0) throw new Error('topRank: empty card set');
  let best = cards[0]!.rank;
  for (let i = 1; i < cards.length; i++) {
    if (RANK_VALUE[cards[i]!.rank] > RANK_VALUE[best]) best = cards[i]!.rank;
  }
  return best;
}

// =============================================================================
// Board-state predicates (used across samplers, classifiers)
// =============================================================================

/** True if the board (any length) contains at least one pair of any rank. */
export function boardIsPaired(board: readonly Card[]): boolean {
  return Array.from(rankCounts(board).values()).some((n) => n >= 2);
}

/** True if the board contains trips of any rank (3 of a kind on the board). */
export function boardHasTrips(board: readonly Card[]): boolean {
  return Array.from(rankCounts(board).values()).some((n) => n >= 3);
}

/** True if the board contains 5+ cards of one suit (a flush is on the board). */
export function flushOnBoard(board: readonly Card[]): boolean {
  return Array.from(suitCounts(board).values()).some((n) => n >= 5);
}

/** True if the board contains exactly 3 cards of one suit (monotone flop). */
export function boardIsMonotone(board: readonly Card[]): boolean {
  return Array.from(suitCounts(board).values()).some((n) => n === board.length);
}

/**
 * True if some 5-card straight is achievable from the board + 2 hole cards.
 *
 * Returns true when at least 3 of any straight's 5 ranks appear on the board
 * (then 2 hole cards can fill the missing ranks). No wheel.
 */
export function straightPossibleFromBoard(board: readonly Card[]): boolean {
  const boardRanks = new Set(board.map((c) => RANK_VALUE[c.rank]));
  for (const high of STRAIGHTS_BY_HIGH) {
    const ranks = straightRanksFor(high);
    const present = ranks.filter((r) => boardRanks.has(r)).length;
    if (present >= 3) return true;
  }
  return false;
}

/**
 * True if some 5-card straight already exists on the board itself (5 distinct
 * board ranks in a row). For board lengths < 5 this is always false.
 */
export function straightOnBoard(board: readonly Card[]): boolean {
  if (board.length < 5) return false;
  const boardRanks = new Set(board.map((c) => RANK_VALUE[c.rank]));
  for (const high of STRAIGHTS_BY_HIGH) {
    const ranks = straightRanksFor(high);
    if (ranks.every((r) => boardRanks.has(r))) return true;
  }
  return false;
}

/**
 * True if a flush is *drawable* from this board:
 *   - Board has 3+ cards of one suit → any 1 hole card of that suit completes
 *     (note: 5+ of suit = flush on board, distinct case).
 *   - Board has 2 cards of one suit → 2 hole cards of that suit completes.
 *
 * Used by the wet/dry classifier and the Flushing-constraint check.
 */
export function flushDrawable(board: readonly Card[]): boolean {
  return Array.from(suitCounts(board).values()).some((n) => n >= 2);
}

/**
 * True if a flush is already makeable from this board with a single hole card
 * (board has 3+ cards of one suit). Used by the board-texture classifier's
 * static-axis hard rule per `/reference/03`.
 */
export function flushAlreadyPresent(board: readonly Card[]): boolean {
  return Array.from(suitCounts(board).values()).some((n) => n >= 3);
}

/**
 * Count of straight-relevant "draw spots" on the board — used as a draw-density
 * input for wet/dry classification. Each straight that has 3+ of its 5 ranks
 * on the board contributes one draw type.
 */
export function straightDrawDensity(board: readonly Card[]): number {
  const boardRanks = new Set(board.map((c) => RANK_VALUE[c.rank]));
  let count = 0;
  for (const high of STRAIGHTS_BY_HIGH) {
    const ranks = straightRanksFor(high);
    const present = ranks.filter((r) => boardRanks.has(r)).length;
    if (present >= 3) count++;
  }
  return count;
}

// =============================================================================
// New-card classification — used by BoardSampler turn/river constraint checks
// =============================================================================

/**
 * True if `newCard` enables a 5-card straight window that was NOT possible
 * before — implements "Straightening" per project owner direction (2026-05-31):
 * "cards that make it possible to have a straight or a new straight."
 *
 * Operational definition: there exists a straight window (5 consecutive ranks)
 * for which the board went from <3 ranks present (straight not achievable with
 * 2 hole cards) to ≥3 ranks present (straight now achievable). If a window
 * already had ≥3 board ranks, adding one more to that same window doesn't
 * "enable" anything new — the straight was already possible.
 */
export function isStraighteningCard(
  boardBefore: readonly Card[],
  newCard: Card,
): boolean {
  const beforeRanks = new Set(boardBefore.map((c) => RANK_VALUE[c.rank]));
  const afterRanks = new Set([...beforeRanks, RANK_VALUE[newCard.rank]]);
  for (const high of STRAIGHTS_BY_HIGH) {
    const ranks = straightRanksFor(high);
    const beforeCount = ranks.filter((r) => beforeRanks.has(r)).length;
    const afterCount = ranks.filter((r) => afterRanks.has(r)).length;
    if (beforeCount < 3 && afterCount >= 3) return true;
  }
  return false;
}

/**
 * True if the 4-card board (boardBefore + newCard) admits a one-card-away
 * straight — i.e. 4 distinct ranks within a 5-rank window, where one
 * additional rank completes the straight.
 *
 * Flop prerequisite: 3 ranks fit within a 5-rank window with at most 1 gap.
 */
export function isOneLinerStraightCard(
  flop: readonly Card[],
  turnCard: Card,
): boolean {
  if (flop.length !== 3) return false;
  const ranks = new Set([...flop, turnCard].map((c) => RANK_VALUE[c.rank]));
  if (ranks.size !== 4) return false; // require 4 distinct ranks (no pairing)
  // Check if 4 of the ranks fall within some 5-rank window of a valid straight.
  for (const high of STRAIGHTS_BY_HIGH) {
    const windowRanks = straightRanksFor(high);
    const present = windowRanks.filter((r) => ranks.has(r)).length;
    if (present === 4) return true;
  }
  return false;
}

/**
 * Flop prerequisite for OneLinerStraight: flop has 3 ranks within a 5-rank
 * window (so adding a 4th can produce a one-card-away straight).
 */
export function flopSupportsOneLinerStraight(flop: readonly Card[]): boolean {
  if (flop.length !== 3) return false;
  const ranks = new Set(flop.map((c) => RANK_VALUE[c.rank]));
  if (ranks.size !== 3) return false;
  for (const high of STRAIGHTS_BY_HIGH) {
    const windowRanks = straightRanksFor(high);
    const present = windowRanks.filter((r) => ranks.has(r)).length;
    if (present === 3) return true;
  }
  return false;
}

/**
 * True if `newCard` pairs a board rank that wasn't paired before. Used for
 * `BoardPairing` and `DoublePair` constraint checks.
 */
export function isBoardPairingCard(
  boardBefore: readonly Card[],
  newCard: Card,
): boolean {
  return boardBefore.some((c) => c.rank === newCard.rank);
}

/**
 * True if `newCard` pairs a *different* rank than the one already paired on
 * the board — creating a board reading as two pair. Used for `DoublePair`
 * constraint check (turn-only).
 *
 * Flop prerequisite: flop has exactly one pair (rank R appears twice, third
 * rank is distinct). Turn matches the unpaired rank.
 */
export function isDoublePairCard(
  flop: readonly Card[],
  turnCard: Card,
): boolean {
  if (flop.length !== 3) return false;
  const counts = rankCounts(flop);
  // Must be a paired flop: one rank with count 2, one rank with count 1.
  const sortedCounts = Array.from(counts.values()).sort((a, b) => b - a);
  if (sortedCounts[0] !== 2 || sortedCounts[1] !== 1) return false;
  // Turn rank must match the unpaired flop rank (not the already-paired one).
  const turnInFlopCount = counts.get(turnCard.rank) ?? 0;
  return turnInFlopCount === 1;
}

/**
 * True if `newCard` brings a 3rd card of its suit to the board. Used for
 * `Flushing` constraint check. (4th card of suit = `FourFlush`, handled
 * separately.)
 */
export function isFlushingCard(
  boardBefore: readonly Card[],
  newCard: Card,
): boolean {
  const suitsBefore = suitCounts(boardBefore);
  return (suitsBefore.get(newCard.suit) ?? 0) === 2;
}

/**
 * True if `newCard` brings a 4th card of its suit to the board (one-card
 * flush possible from any hole card of that suit). Used for `FourFlush`
 * constraint check (turn-only).
 *
 * Flop prerequisite: monotone flop (3 cards of one suit).
 */
export function isFourFlushCard(
  flop: readonly Card[],
  turnCard: Card,
): boolean {
  if (flop.length !== 3) return false;
  if (!boardIsMonotone(flop)) return false;
  const flopSuit = flop[0]!.suit; // all same when monotone
  return turnCard.suit === flopSuit;
}
